import { Storage } from '@aws-amplify/storage'
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import axios from '../../../axios'
import { ENDPOINTS } from '../../../constants'
import GenImageUploader from '../../genImageUpload'
import SbSettingToggle from './SbSettingToggle'
import { SbFolder } from './type'
import useSbSettings from './useSbSettings'

export interface SettingsModalProps extends DialogProps {
  folderId: string
  username: string
  folder: SbFolder
  onUpdate: (updated: SbFolder) => void
  onClose: () => void
}

export default function SettingsModal({
  folderId,
  username,
  folder,
  open,
  onClose,
  onUpdate,
}: SettingsModalProps) {
  const [folderImage, setFolderImage] = useState(folder.displayImage)
  const [isUploadingImg, setIsUploadingImg] = useState(false)

  const updateSbFolder = async (
    property: keyof SbFolder,
    newValue: number | string | boolean | null,
    successMessage: string = 'settings updated'
  ) => {
    try {
      await axios.patch(ENDPOINTS.SBFolder.replace(':id', folderId), {
        [property]: newValue,
      })
      onUpdate({ ...folder, [property]: newValue })
      toast.success(successMessage)
    } catch (e) {
      toast.error('failed to change settings')
    }
  }

  const onUploadImage = async (files: any) => {
    try {
      setIsUploadingImg(true)

      if (files) {
        const image = files[0]
        if (!image) {
          throw new Error('image not supported')
        }

        const imageName = `sbfol-${uuidv4()}${image.name}`

        const result = await Storage.put(imageName, image, {
          level: 'public',
          contentType: image.type || 'image/gif, image/jpeg, image/png',
        })

        const imageUrl = `https://${process.env.GATSBY_S3_BUCKET_NAME}.s3.us-east-2.amazonaws.com/public/${result.key}`

        setFolderImage(imageUrl)
        await updateSbFolder('displayImage', imageUrl, 'folder image updated')
      }
    } catch (err) {
      toast.error('failed to update image')
    } finally {
      setIsUploadingImg(false)
    }
  }

  const onDeleteImage = async () => {
    try {
      setIsUploadingImg(true)

      setFolderImage(null)
      await updateSbFolder('displayImage', null, 'folder image deleted')
    } catch (err) {
      toast.error('failed to delete image')
    } finally {
      setIsUploadingImg(false)
    }
  }

  const { canEdit, canForward, settings } = useSbSettings(folder, username)

  if (!canEdit) {
    return null
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="sbFolderSettingsModal"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography variant="subtitle1">manage folder</Typography>

          <IconButton size="small" aria-label="close-modal" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: '0.5rem 2rem 3rem 2rem' }}>
        {/* Settings */}
        <Grid container spacing={1} style={{ minWidth: '250px' }}>
          {settings.map(setting => (
            <Grid item xs={12} md={6}>
              <SbSettingToggle {...setting} onUpdate={updateSbFolder} />
            </Grid>
          ))}
        </Grid>

        {canForward && (
          <Box style={{ marginTop: '2.5rem' }}>
            {/* Folder Image */}
            <GenImageUploader
              imageSrc={folderImage}
              label="folder image"
              onUpload={onUploadImage}
              onDelete={onDeleteImage}
              isUploading={isUploadingImg}
              dialogState={open ? 'open' : 'closed'}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
